// components/CommunityAlertBanner.js
//
// Renders the active community alerts at the top of a screen. Multi-alert
// (sorted by tier desc), severity-colored per the tier palette, with a
// per-session dismiss for dismissible tiers.
//
// Reusable across surfaces — Phase 1 mounts on the resident shell
// (App.modular.js); Phase 2 will mount on /haulers (HaulerApp.js)
// without changes to this component.
//
// Per-session dismiss only — localStorage key
//   loaddash_dismissed_alert_<id>
// Persistent dismissal could ship later with a `community_alert_reads`
// table, out of scope for Phase 1.

import React, { useEffect, useState } from "react";
import { TIERS, tierConfig } from "../services/communityAlertsService";

const DISMISS_KEY_PREFIX = "loaddash_dismissed_alert_";

function isDismissed(alertId) {
  if (typeof window === "undefined") return false;
  try { return localStorage.getItem(DISMISS_KEY_PREFIX + alertId) === "1"; }
  catch (_) { return false; }
}

function markDismissed(alertId) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(DISMISS_KEY_PREFIX + alertId, "1"); }
  catch (_) {}
}

export default function CommunityAlertBanner({ alerts = [], style = {}, audienceContext = "resident" }) {
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  useEffect(() => {
    // Re-hydrate dismissed set when alerts list changes — covers the case
    // where a freshly-published alert (not yet dismissed) appears alongside
    // a previously-dismissed one.
    const next = new Set();
    for (const a of alerts) {
      if (isDismissed(a.id)) next.add(a.id);
    }
    setDismissedIds(next);
  }, [alerts]);

  const visible = alerts.filter(a => !dismissedIds.has(a.id));
  if (visible.length === 0) return null;

  function handleDismiss(alertId) {
    markDismissed(alertId);
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(alertId);
      return next;
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      {visible.map(alert => (
        <BannerRow key={alert.id} alert={alert} onDismiss={handleDismiss} audienceContext={audienceContext} />
      ))}
    </div>
  );
}

function BannerRow({ alert, onDismiss, audienceContext = "resident" }) {
  const cfg = tierConfig(alert.tier);
  const showDismiss = !!alert.dismissible;
  const isPause = !!alert.pauses_service;
  // Hauler-side Tier 3 clarification (Phase 2). Phase 3 will gate
  // booking-flow enforcement; this microcopy gives Haulers context
  // even before the enforcement wires so they understand what a
  // "PLATFORM PAUSED" pill means for their in-flight work.
  const showHaulerPauseHint = isPause && audienceContext === "driver";

  return (
    <div
      role="alert"
      style={{
        background: cfg.bg,
        borderLeft: `4px solid ${cfg.accent}`,
        borderRadius: 10,
        padding: "12px 14px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        boxShadow: "0 1px 3px rgba(0,0,0,.08)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
          flexWrap: "wrap",
        }}>
          <span style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            background: cfg.pillBg,
            color: cfg.pillText,
            padding: "2px 7px",
            borderRadius: 999,
          }}>
            {isPause ? "Platform Paused" : cfg.name}
          </span>
        </div>
        <div style={{
          fontSize: 14,
          fontWeight: 700,
          color: cfg.text,
          lineHeight: 1.3,
        }}>
          {alert.title}
        </div>
        <div style={{
          fontSize: 13,
          color: cfg.text,
          marginTop: 4,
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
        }}>
          {alert.body}
        </div>
        {showHaulerPauseHint && (
          <div style={{
            fontSize: 12,
            color: cfg.text,
            opacity: 0.78,
            marginTop: 6,
            lineHeight: 1.4,
            fontWeight: 600,
          }}>
            New booking discovery is paused. Your active jobs continue normally.
          </div>
        )}
        {alert.reason && (
          <div style={{
            fontSize: 11,
            color: cfg.text,
            opacity: 0.72,
            marginTop: 6,
            fontStyle: "italic",
          }}>
            Reason: {alert.reason}
          </div>
        )}
      </div>

      {showDismiss && (
        <button
          type="button"
          onClick={() => onDismiss(alert.id)}
          aria-label="Dismiss"
          title="Dismiss"
          style={{
            flex: "0 0 auto",
            width: 28,
            height: 28,
            borderRadius: 999,
            border: `1px solid ${cfg.accent}`,
            background: "transparent",
            color: cfg.accent,
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 800,
            cursor: "pointer",
            lineHeight: 1,
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// Convenience export: TIERS palette so consumers can label things
// consistently outside of the banner itself (e.g., admin composer).
export { TIERS };
